import { useNavigate, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"

function pagination(currentPage: number, pageCount: number, deltaValue: number) {
  const delta = deltaValue
  const range: number[] = []
  const rangeWithDots: Array<string | number> = []
  let l

  range.push(1)

  if (pageCount <= 1) return range

  for (let i = currentPage - delta; i <= currentPage + delta; i++)
    if (i < pageCount && i > 1) range.push(i)

  range.push(pageCount)

  for (const i of range) {
    if (l)
      if (i - l === 2) rangeWithDots.push(l + 1)
      else if (i - l !== 1) rangeWithDots.push("...")

    rangeWithDots.push(i)
    l = i
  }

  return rangeWithDots
}

interface PaginatorProps {
  page: number
  pageCount: number
}

const Paginator = ({ page, pageCount }: PaginatorProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  if (pageCount <= 1) return null

  const items = pagination(page, pageCount, 2)

  const handlePageChange = (newPage: number) => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set("page", newPage.toString())
    navigate(`${location.pathname}?${searchParams.toString()}`)
  }

  return (
    <div className="flex items-center justify-between" data-testid="pagination">
      <p className="text-sm text-muted-foreground">
        Page {page} of {pageCount}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 cursor-pointer gap-1 px-2.5"
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          data-testid="pagination-prev"
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </Button>

        {items.map((item, idx) => {
          if (item === "...")
            return (
              <span key={`dots-${idx}`} className="px-1 text-sm text-muted-foreground">
                ...
              </span>
            )

          return (
            <Button
              key={item}
              variant={item === page ? "default" : "ghost"}
              size="sm"
              className={cn("h-8 w-8 cursor-pointer p-0 text-sm")}
              onClick={() => handlePageChange(item as number)}
              data-testid={`pagination-page-${item}`}
            >
              {item}
            </Button>
          )
        })}

        <Button
          variant="outline"
          size="sm"
          className="h-8 cursor-pointer gap-1 px-2.5"
          onClick={() => handlePageChange(page + 1)}
          disabled={page === pageCount}
          data-testid="pagination-next"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export default Paginator
